// src/errors.ts
var OxboAuthError = class extends Error {
  constructor(status, body, message) {
    super(message ?? `OXBO Auth: HTTP ${status}`);
    this.name = "OxboAuthError";
    this.status = status;
    this.body = body;
  }
  /** True when the token is missing or expired. */
  isUnauthorized() {
    return this.status === 401;
  }
  /** True when the caller lacks permission. */
  isForbidden() {
    return this.status === 403;
  }
  /** True when the request body fails validation. */
  isValidationError() {
    return this.status === 422;
  }
  /** True when rate-limited. */
  isRateLimited() {
    return this.status === 429;
  }
};

// src/client.ts
var DEFAULT_TIMEOUT_MS = 15e3;
var OxboAuthClient = class {
  constructor(config) {
    this.baseUrl = config.baseUrl.replace(/\/$/, "");
    this.fetchImpl = config.fetch ?? globalThis.fetch?.bind(globalThis);
    if (!this.fetchImpl) {
      throw new Error(
        "OxboAuthClient: no fetch implementation found. Pass one via config.fetch (e.g. node-fetch) or use Node \u226518."
      );
    }
    this.getAccessToken = config.getAccessToken;
    this.timeoutMs = config.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  }
  // -------------------------------------------------------------------------
  // Internal helpers
  // -------------------------------------------------------------------------
  async resolveToken() {
    if (!this.getAccessToken) return null;
    const tok = await this.getAccessToken();
    return tok ?? null;
  }
  /**
   * Core request method. Handles auth headers, timeouts, and error mapping.
   * Returns the parsed JSON body for 2xx responses with a body, or `null`
   * for 204 No Content.
   */
  async request(method, path, options = {}) {
    const { body, params, authenticated = true } = options;
    const url = new URL(`${this.baseUrl}${path}`);
    if (params) {
      for (const [key, val] of Object.entries(params)) {
        if (val !== void 0) url.searchParams.set(key, val);
      }
    }
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json"
    };
    if (authenticated) {
      const token = await this.resolveToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);
    const init = {
      method,
      headers,
      signal: controller.signal,
      credentials: "include"
      // send/receive cookies for session-cookie flows
    };
    if (body !== void 0) {
      init.body = JSON.stringify(body);
    }
    let response;
    try {
      response = await this.fetchImpl(url.toString(), init);
    } finally {
      clearTimeout(timer);
    }
    if (response.status === 204) {
      return void 0;
    }
    let parsed;
    const ct = response.headers.get("Content-Type") ?? "";
    if (ct.includes("application/json")) {
      parsed = await response.json();
    } else {
      parsed = await response.text();
    }
    if (!response.ok) {
      throw new OxboAuthError(response.status, parsed);
    }
    return parsed;
  }
  get(path, opts) {
    return this.request("GET", path, opts);
  }
  post(path, body, opts) {
    return this.request("POST", path, { body, ...opts });
  }
  put(path, body) {
    return this.request("PUT", path, { body });
  }
  delete(path) {
    return this.request("DELETE", path);
  }
  // -------------------------------------------------------------------------
  // Registration
  // -------------------------------------------------------------------------
  /** POST /v1/auth/register */
  register(data) {
    return this.post("/v1/auth/register", data, {
      authenticated: false
    });
  }
  /** GET /v1/auth/verify-email?token= */
  verifyEmail(token) {
    return this.get("/v1/auth/verify-email", {
      params: { token },
      authenticated: false
    });
  }
  // -------------------------------------------------------------------------
  // Auth
  // -------------------------------------------------------------------------
  /** POST /v1/auth/login */
  login(data) {
    return this.post("/v1/auth/login", data, {
      authenticated: false
    });
  }
  /** POST /v1/auth/logout — clears session cookies + blocklists jti */
  logout() {
    return this.post("/v1/auth/logout");
  }
  /** POST /v1/auth/refresh */
  refreshToken(data) {
    return this.post("/v1/auth/refresh", data, {
      authenticated: false
    });
  }
  // -------------------------------------------------------------------------
  // Password Reset
  // -------------------------------------------------------------------------
  /**
   * POST /v1/auth/password-reset/request
   * Always 204 (enumeration-safe — no indication if email is registered).
   */
  requestPasswordReset(data) {
    return this.post("/v1/auth/password-reset/request", data, {
      authenticated: false
    });
  }
  /** POST /v1/auth/password-reset/confirm */
  confirmPasswordReset(data) {
    return this.post("/v1/auth/password-reset/confirm", data, {
      authenticated: false
    });
  }
  // -------------------------------------------------------------------------
  // OAuth
  // -------------------------------------------------------------------------
  /** POST /v1/auth/oauth/google/login — returns authorize_url + state */
  googleLogin(data) {
    return this.post("/v1/auth/oauth/google/login", data, {
      authenticated: false
    });
  }
  /** POST /v1/auth/oauth/microsoft/login — returns authorize_url + state */
  microsoftLogin(data) {
    return this.post(
      "/v1/auth/oauth/microsoft/login",
      data,
      { authenticated: false }
    );
  }
  /**
   * Build the Google OAuth callback URL for manual redirect handling.
   * The callback itself is a browser redirect — the SPA does not call this
   * endpoint directly; it merely follows the authorize_url and the server
   * handles the ?code= exchange.
   */
  googleCallbackUrl(params) {
    const url = new URL(`${this.baseUrl}/v1/auth/oauth/google/callback`);
    if (params.code) url.searchParams.set("code", params.code);
    if (params.state) url.searchParams.set("state", params.state);
    if (params.error) url.searchParams.set("error", params.error);
    return url.toString();
  }
  // -------------------------------------------------------------------------
  // Session / User identity
  // -------------------------------------------------------------------------
  /** GET /v1/users/me — returns identity from JWT (no DB call) */
  getMe() {
    return this.get("/v1/users/me");
  }
  // -------------------------------------------------------------------------
  // Feature Flags
  // -------------------------------------------------------------------------
  /**
   * GET /v1/users/me/features
   * Returns {features: {[key: string]: boolean}}.
   * Cache-Control: private, max-age=60 — responses are short-lived.
   */
  getFeatures() {
    return this.get("/v1/users/me/features");
  }
  /** PUT /v1/admin/org-features/{feature_key} — create or update override */
  upsertFeatureOverride(featureKey, data) {
    return this.put(
      `/v1/admin/org-features/${encodeURIComponent(featureKey)}`,
      data
    );
  }
  /** DELETE /v1/admin/org-features/{feature_key} — revert to catalog default */
  deleteFeatureOverride(featureKey) {
    return this.delete(
      `/v1/admin/org-features/${encodeURIComponent(featureKey)}`
    );
  }
  // -------------------------------------------------------------------------
  // Invitations
  // -------------------------------------------------------------------------
  /** POST /v1/auth/invitations/ — requires owner or admin JWT */
  createInvitation(data) {
    return this.post("/v1/auth/invitations/", data);
  }
  /** GET /v1/auth/invitations/?status= — requires owner or admin JWT */
  listInvitations(status = "pending") {
    return this.get("/v1/auth/invitations/", {
      params: { status }
    });
  }
  /**
   * DELETE /v1/auth/invitations/{invitation_id}
   * Requires owner or admin JWT.
   */
  revokeInvitation(invitationId) {
    return this.delete(
      `/v1/auth/invitations/${encodeURIComponent(invitationId)}`
    );
  }
  /**
   * GET /v1/auth/invitations/{token}/info
   * No auth required — used by the accept/decline SPA flow.
   */
  getInvitationInfo(token) {
    return this.get(
      `/v1/auth/invitations/${encodeURIComponent(token)}/info`,
      { authenticated: false }
    );
  }
  /**
   * POST /v1/auth/invitations/{token}/accept
   * No auth required. Pass password for Branch C (new-user) flow.
   */
  acceptInvitation(token, data = {}) {
    return this.post(
      `/v1/auth/invitations/${encodeURIComponent(token)}/accept`,
      data,
      { authenticated: false }
    );
  }
  // -------------------------------------------------------------------------
  // Access Requests
  // -------------------------------------------------------------------------
  /** POST /v1/auth/access-requests/ — authenticated user requests to join an org */
  createAccessRequest(data) {
    return this.post("/v1/auth/access-requests/", data);
  }
  /** GET /v1/auth/access-requests/?status= — requires owner or admin JWT */
  listAccessRequests(status = "pending") {
    return this.get("/v1/auth/access-requests/", {
      params: { status }
    });
  }
  /**
   * POST /v1/auth/access-requests/{request_id}/approve
   * Requires owner or admin JWT.
   */
  approveAccessRequest(requestId, data) {
    return this.post(
      `/v1/auth/access-requests/${encodeURIComponent(requestId)}/approve`,
      data
    );
  }
  /**
   * POST /v1/auth/access-requests/{request_id}/reject
   * Requires owner or admin JWT.
   */
  rejectAccessRequest(requestId, data = {}) {
    return this.post(
      `/v1/auth/access-requests/${encodeURIComponent(requestId)}/reject`,
      data
    );
  }
  // -------------------------------------------------------------------------
  // Health / meta
  // -------------------------------------------------------------------------
  /** GET /health — liveness probe */
  health() {
    return this.get("/health", { authenticated: false });
  }
};

// src/feature-cache.ts
function createFeatureCache() {
  let entry;
  return {
    get(ttlMs) {
      if (!entry) return void 0;
      if (Date.now() - entry.fetchedAt > ttlMs) return void 0;
      return entry;
    },
    set(features) {
      entry = { features, fetchedAt: Date.now() };
    },
    clear() {
      entry = void 0;
    }
  };
}
var DEFAULT_FEATURE_TTL_MS = 6e4;

export { DEFAULT_FEATURE_TTL_MS, OxboAuthClient, OxboAuthError, createFeatureCache };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map